import { useEffect, useState, type FormEvent } from 'react'
import {
  deleteRosterMember,
  fetchAudit,
  fetchEngines,
  fetchRosterMembers,
  revokeMemberSessions,
  saveRosterMember,
} from '../data/api'
import type { AuditEntry, Member } from '../types'
import type { Role } from '../data/session'
import { SectionLabel } from './RoundTimeline'
import { fmtStamp } from '../data/fmt'

// The roster IS the access list: an account that isn't here cannot sign in at all. Admin-only, and
// the API says so too — every route behind this page 403s for anyone else.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

const ROLE_NOTE: Record<Role, string> = {
  admin: 'Everything a coach can do, plus this page and AI spend.',
  coach: 'Regenerate AI reports, delete demos and scouting folders, tag matches.',
  player: 'Read the portal, upload demos, and see their own AI feedback.',
}

/** The API's refusals, in words a person can act on. */
const ERRORS: Record<string, string> = {
  bad_steamid: 'That doesn’t look like a SteamID64 — it’s the 17-digit one (steamid.io will tell you).',
  bad_role: 'Unknown role.',
  last_admin: 'That’s the last admin. Promote someone else first, or the portal locks itself.',
  bootstrap_admin_is_fixed:
    'That account is the bootstrap admin from app settings — change BOOTSTRAP_ADMIN_STEAMID64 instead.',
  forbidden: 'Admins only.',
  not_found: 'Not on the roster.',
}
const say = (e: unknown) => ERRORS[(e as Error).message] ?? `Couldn’t save that (${(e as Error).message}).`

export function Admin() {
  const [members, setMembers] = useState<Member[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [sid, setSid] = useState('')
  const [nick, setNick] = useState('')
  const [role, setRole] = useState<Role>('player')
  const [busy, setBusy] = useState(false)

  const load = () =>
    fetchRosterMembers()
      .then((m) => {
        setMembers(m)
        setErr(null)
      })
      .catch((e) => setErr(say(e)))

  useEffect(() => {
    load()
  }, [])

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await saveRosterMember(sid.trim(), role, nick.trim())
      setSid('')
      setNick('')
      await load()
    } catch (e) {
      setErr(say(e))
    } finally {
      setBusy(false)
    }
  }

  const change = async (m: Member, next: Role) => {
    try {
      await saveRosterMember(m.steamid64, next)
      await load()
    } catch (e) {
      setErr(say(e))
    }
  }

  /** Rename in place. Empty clears it, falling back to whatever Steam says they're called. */
  const rename = async (m: Member, next: string) => {
    if (next.trim() === (m.nick ?? '')) return
    try {
      await saveRosterMember(m.steamid64, m.role, next.trim())
      await load()
    } catch (e) {
      setErr(say(e))
    }
  }

  const remove = async (m: Member) => {
    if (!confirm(`Remove ${m.name ?? m.steamid64}? They lose access within a minute.`)) return
    try {
      await deleteRosterMember(m.steamid64)
      await load()
    } catch (e) {
      setErr(say(e))
    }
  }

  const signOut = async (m: Member) => {
    const who = m.nick ?? m.name ?? m.steamid64
    if (!confirm(`Sign ${who} out of every device? They keep their access and can sign back in.`)) return
    try {
      await revokeMemberSessions(m.steamid64)
      await load()
    } catch (e) {
      setErr(say(e))
    }
  }

  return (
    <div className="space-y-5">
      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="A1" label="Who can sign in" />
        <p className="mt-2 text-xs leading-relaxed text-text-faint">
          Steam sign-in is allowlist-only — an account that isn’t on this list is told to ask its
          coach. Removing someone here takes effect within a minute, even if they’re signed in.
        </p>

        {/* Errors here are the result of an action just taken, so they must be announced. */}
        {err && (
          <p role="alert" className="mt-3 text-xs text-loss">
            {err}
          </p>
        )}

        <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-60">
            <span className="mb-1 block text-[10px] uppercase tracking-wider text-text-faint">SteamID64</span>
            <input
              value={sid}
              onChange={(e) => setSid(e.target.value)}
              placeholder="76561198…"
              inputMode="numeric"
              className="w-full border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            />
          </label>
          <label className="min-w-36">
            <span className="mb-1 block text-[10px] uppercase tracking-wider text-text-faint">Nickname</span>
            <input
              value={nick}
              onChange={(e) => setNick(e.target.value)}
              placeholder="tonza"
              maxLength={32}
              className="w-full border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            />
          </label>
          <label>
            <span className="mb-1 block text-[10px] uppercase tracking-wider text-text-faint">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="border border-border bg-bg-2 px-3 py-2 text-sm text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            >
              <option value="player">player</option>
              <option value="coach">coach</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={busy || !sid.trim()}
            className="font-display px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-50"
            style={{ borderRadius: 'var(--radius)', background: 'var(--accent)', color: 'var(--accent-ink)' }}
          >
            {busy ? 'Saving…' : 'Add / update'}
          </button>
        </form>
        <p className="mt-2 text-[11px] text-text-faint">
          {ROLE_NOTE[role]} The nickname is what the portal calls them everywhere — in-game names and
          FACEIT nicks change constantly, this one doesn’t. Leave it blank to use their Steam name.
        </p>
      </section>

      <section className="border border-border bg-surface p-5" style={card}>
        <SectionLabel n="A2" label="Roster" />
        {!members ? (
          <p className="mt-3 text-sm text-text-dim">Loading…</p>
        ) : members.length === 0 ? (
          <p className="mt-3 text-sm text-text-faint">Nobody yet — add the five and the coach above.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                  <th className="px-2 py-1.5 text-left font-medium">Nickname</th>
                  <th className="px-2 py-1.5 text-left font-medium">Steam name</th>
                  <th className="px-2 py-1.5 text-left font-medium">SteamID64</th>
                  <th className="px-2 py-1.5 text-left font-medium">Role</th>
                  <th className="px-2 py-1.5 text-left font-medium">Last seen</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.steamid64} className="border-t border-border/60">
                    <td className="px-2 py-2 text-text">
                      {/* Editable in place — a rename is a one-field edit, not a form. */}
                      <input
                        aria-label={`Nickname for ${m.name ?? m.steamid64}`}
                        defaultValue={m.nick ?? ''}
                        onBlur={(e) => rename(m, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        placeholder="—"
                        maxLength={32}
                        className="w-28 border border-transparent bg-transparent px-1.5 py-1 text-sm text-text outline-none hover:border-border focus:border-accent"
                        style={{ borderRadius: 'var(--radius)' }}
                      />
                      {m.addedBy === 'bootstrap' && (
                        <span className="ml-1 text-[10px] text-text-faint" title="From BOOTSTRAP_ADMIN_STEAMID64">
                          bootstrap
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-[11px] text-text-dim">
                      {/* Only ever filled in when a Steam Web API key is configured — its absence
                          says nothing about whether they've signed in. lastSeen says that. */}
                      {m.name ?? <span className="text-text-faint">—</span>}
                    </td>
                    <td className="px-2 py-2 font-mono text-[11px] tabular-nums text-text-dim">{m.steamid64}</td>
                    <td className="px-2 py-2">
                      <select
                        aria-label={`Role for ${m.nick ?? m.name ?? m.steamid64}`}
                        value={m.role}
                        onChange={(e) => change(m, e.target.value as Role)}
                        className="border border-border bg-bg-2 px-2 py-1 text-xs text-text-dim outline-none"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        <option value="player">player</option>
                        <option value="coach">coach</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td className="px-2 py-2 text-[11px] text-text-faint">
                      {m.lastSeen ? fmtStamp(m.lastSeen) : <span className="text-text-faint">never signed in</span>}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {/* Sign out ≠ remove. Removal takes their access with it, and the usual case
                          ("they left a laptop on a train") wants them back in five minutes on a
                          machine they trust — so this keeps the account and kills the sessions. */}
                      <button
                        onClick={() => signOut(m)}
                        aria-label={`Sign ${m.nick ?? m.name ?? m.steamid64} out of every device`}
                        className="mr-3 text-[11px] text-text-faint hover:text-accent"
                      >
                        sign out
                      </button>
                      <button
                        onClick={() => remove(m)}
                        aria-label={`Remove ${m.nick ?? m.name ?? m.steamid64} from the roster`}
                        className="text-[11px] text-text-faint hover:text-loss"
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AuditLog />
      <Engines />
    </div>
  )
}

/** What was deleted, and by whom. Nothing recorded this while there was one shared password —
 *  there was nobody to attribute it to. Now a deleted match takes its notes with it, and somebody
 *  should be able to find out who did that without asking five people. */
function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  useEffect(() => {
    fetchAudit()
      .then(setEntries)
      .catch(() => setEntries([]))
  }, [])

  // Deliberately NOT hidden when empty. A section that vanishes makes "nothing has been deleted"
  // and "the audit stopped recording" look identical — and the whole value of an audit trail is
  // being able to tell those apart. An empty one that says so is doing its job.
  if (!entries) return null // still loading
  return (
    <section className="border border-border bg-surface p-5" style={card}>
      <SectionLabel n="A3" label="What's been deleted" />
      {entries.length === 0 && (
        <p className="mt-3 text-sm text-text-faint">
          Nothing yet. Deletions — matches, scouting folders, notes — roster changes and forfeit
          marks are recorded here for three months. An empty list means none have happened, not that
          nothing is being watched.
        </p>
      )}
      <p className="mt-2 text-xs text-text-faint">
        Deletes, roster changes, and forfeit marks. Last three months, newest first.
      </p>
      <div className="mt-3 space-y-1">
        {entries.slice(0, 40).map((e, i) => (
          <div key={`${e.at}-${i}`} className="flex flex-wrap items-baseline gap-2 text-[11px]">
            <span className="w-32 shrink-0 text-text-faint">{fmtStamp(e.at)}</span>
            <span className="font-display uppercase tracking-wider text-text-dim">{e.actor}</span>
            <span
              className="rounded px-1.5 py-px uppercase tracking-wider"
              style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
            >
              {e.action.replace(/\./g, ' ')}
            </span>
            <span className="truncate font-mono text-text-faint" title={e.target}>
              {e.target.length > 20 ? `${e.target.slice(0, 8)}…` : e.target}
            </span>
            {e.detail && <span className="truncate text-text-faint">{e.detail}</span>}
          </div>
        ))}
      </div>
    </section>
  )
}

/** Which parser version each demo's numbers came from.
 *
 *  A parser fix changes what a demo means, and nothing else in the portal can tell: the artifacts
 *  look the same and the cache stamps only say something was rewritten, never that it should be.
 *  More than one row here means a reparse would actually change numbers. */
function Engines() {
  const [data, setData] = useState<{ groups: { engine: string; count: number }[]; current: string } | null>(null)
  useEffect(() => {
    fetchEngines()
      .then(setData)
      .catch(() => setData(null))
  }, [])

  if (!data || data.groups.length < 2) return null // one engine, nothing to say
  return (
    <section className="border p-5" style={{ ...card, borderColor: 'var(--loss)' }}>
      <SectionLabel n="A4" label="Demos parsed by an older engine" />
      <p className="mt-2 text-xs leading-relaxed text-text-faint">
        These demos hold numbers from a previous parser. Reparsing them is what makes the season
        comparable — until then, older matches and newer ones were measured slightly differently.
      </p>
      <div className="mt-3 space-y-1">
        {data.groups.map((g) => (
          <div key={g.engine} className="flex items-baseline gap-3 text-[11px]">
            <span className="w-28 font-mono text-text-dim">{g.engine}</span>
            <span className="text-text">{g.count} demo{g.count === 1 ? '' : 's'}</span>
            {g.engine === data.current && <span className="text-text-faint">current</span>}
          </div>
        ))}
      </div>
    </section>
  )
}
